/**
 * Advisor Bonuses Module - Pure calculation functions for advisor effects
 *
 * This module provides a single source of truth for calculating advisor bonuses,
 * penalties, and special abilities. Used by both GameContext and AdvisorsModal.
 */

import { BALANCE } from '../../data';

/**
 * Calculate aggregated bonuses, penalties, and specials from active advisors
 * @param {string[]} activeAdvisorIds - Array of active advisor IDs
 * @param {Object[]} [advisorData] - Optional advisor data array (defaults to BALANCE.advisors)
 * @returns {{ bonuses: Object, penalties: Object, specials: Object }}
 */
export function calculateAdvisorBonuses(activeAdvisorIds, advisorData = BALANCE.advisors) {
  const bonuses = {};
  const penalties = {};
  const specials = {
    buildingCost: 0,      // Negative = cheaper buildings
    researchSpeed: 0,     // Positive = faster research
    clickPower: 0,        // Positive = more per click
  };

  if (!activeAdvisorIds || !Array.isArray(activeAdvisorIds)) {
    return { bonuses, penalties, specials };
  }

  activeAdvisorIds.forEach(advisorId => {
    const advisor = advisorData.find(a => a.id === advisorId);
    if (!advisor) return;

    // Standard bonuses (e.g., food: 0.50)
    Object.entries(advisor.bonuses || {}).forEach(([key, val]) => {
      bonuses[key] = (bonuses[key] || 0) + val;
    });

    // Standard penalties (e.g., knowledge: -0.20)
    Object.entries(advisor.penalties || {}).forEach(([key, val]) => {
      penalties[key] = (penalties[key] || 0) + val;
    });

    // Special abilities (e.g., { type: 'buildingCost', value: -0.20 })
    if (advisor.special) {
      const { type, value } = advisor.special;
      if (Object.prototype.hasOwnProperty.call(specials, type)) {
        specials[type] += value;
      }
    }
  });

  return { bonuses, penalties, specials };
}

/**
 * Get totals for display purposes (includes special descriptions)
 * @param {string[]} activeAdvisorIds - Array of active advisor IDs
 * @param {Object[]} [advisorData] - Optional advisor data array
 * @returns {{ bonuses: Object, penalties: Object, specials: Object[] }}
 */
export function getAdvisorTotalsForDisplay(activeAdvisorIds, advisorData = BALANCE.advisors) {
  const totals = { bonuses: {}, penalties: {}, specials: [] };

  if (!activeAdvisorIds || !Array.isArray(activeAdvisorIds)) {
    return totals;
  }

  activeAdvisorIds.forEach(advisorId => {
    const advisor = advisorData.find(a => a.id === advisorId);
    if (!advisor) return;

    Object.entries(advisor.bonuses || {}).forEach(([key, val]) => {
      totals.bonuses[key] = (totals.bonuses[key] || 0) + val;
    });

    Object.entries(advisor.penalties || {}).forEach(([key, val]) => {
      totals.penalties[key] = (totals.penalties[key] || 0) + val;
    });

    if (advisor.special) {
      totals.specials.push(advisor.special);
    }
  });

  return totals;
}
